import * as React from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Checkbox, Collapse, IconButton, Box, Typography, Skeleton } from "@mui/material";
import { styled } from "@mui/material/styles";
import Paginator from "../../pagination";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export const PaginationContext: any = React.createContext({});

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  border: "1px solid #E5E7EB",
  overflow: "hidden",
  "&::-webkit-scrollbar": {
    height: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#F3F4F6",
    borderRadius: "4px",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#D1D5DB",
    borderRadius: "4px",
    "&:hover": {
      background: "#9CA3AF",
    },
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#F8FAFC",
    color: "#374151",
    fontWeight: 600,
    fontSize: "14px",
    lineHeight: "20px",
    padding: "12px 16px",
    borderBottom: "2px solid #E5E7EB",
    textTransform: "none",
    letterSpacing: "0.025em",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: "14px",
    lineHeight: "20px",
    color: "#1F2937",
    padding: "12px 16px",
    borderBottom: "1px solid #F3F4F6",
    verticalAlign: "middle",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#F9FAFB",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#FAFAFA",
  },
  "&:nth-of-type(even):hover": {
    backgroundColor: "#F3F4F6",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "48px 24px",
  textAlign: "center",
}));

const EmptyStateIcon = styled(Box)(({ theme }) => ({
  width: "64px",
  height: "64px",
  borderRadius: "50%",
  backgroundColor: "#F3F4F6",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "16px",
  "&::before": {
    content: '"📊"',
    fontSize: "24px",
  },
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  color: "#6B7280",
  fontSize: "14px",
  fontWeight: 500,
}));

function LoadingText() {
  return (
    <LoadingContainer>
      <Typography variant="body2" sx={{ color: "#6B7280" }}>
        Loading...
      </Typography>
    </LoadingContainer>
  );
}

const DEFAULT_SKELETON_ROW_COUNT = 8;
const MAX_SKELETON_ROW_COUNT = 10;

function getSkeletonRowCount(selectedPageSize?: number) {
  const requestedCount = selectedPageSize || DEFAULT_SKELETON_ROW_COUNT;
  return Math.min(requestedCount, MAX_SKELETON_ROW_COUNT);
}

function SkeletonRow({ columns }) {
  return (
    <StyledTableRow>
      {columns.map((columnItem, i) => (
        <StyledTableCell key={`skeleton-col-${i}`} align={columnItem.align}>
          <Skeleton variant="text" width={columnItem.width || "80%"} height={20} />
        </StyledTableCell>
      ))}
    </StyledTableRow>
  );
}

export default function SimpleTable<T>({
  columns = [],
  rows,
  total,
  onPageSettingsChange,
  tdRenderer,
  showPageSize = true,
  selectedPageSize,
  selectedPage,
  renderExpandedRowData,
  exandableRow = false,
  onRowSelect,
  selectedRows = {
    field: "id",
    values: [],
  },
  fetching,
  showPagination = true,
  defaultExpandAll = false,
}: any) {
  const onPageSizeChange = (newValue) => {
    onPageSettingsChange({
      selectedPageSize: newValue,
      selectedPage: 1,
    });
  };

  const onPageChange = (newValue) => {
    onPageSettingsChange({
      selectedPageSize,
      selectedPage: newValue,
    });
  };

  function Row(props) {
    const { row, index } = props;
    const [open, setOpen] = React.useState(defaultExpandAll);

    return (
      <React.Fragment>
        <StyledTableRow
          sx={{ 
            "& > *": { 
              borderBottom: exandableRow && !open ? "none" : "unset" 
            } 
          }}
        >
          {columns.map((columnItem, i) => {
            switch (columnItem["field"]) {
              case "checkbox":
                return (
                  <StyledTableCell padding="checkbox" key={`columnItem-${i}`}>
                    <Checkbox
                      onClick={(event) =>
                        onRowSelect({ event, row, index, column: columnItem })
                      }
                      sx={{
                        color: "#D1D5DB",
                        "&.Mui-checked": {
                          color: "#070639",
                        },
                        "&:hover": {
                          backgroundColor: "rgba(7, 6, 57, 0.04)",
                        },
                      }}
                      checked={
                        selectedRows.values.indexOf(row[selectedRows.field]) !==
                        -1
                      }
                    />
                  </StyledTableCell>
                );
                break;
              case "expandable":
                return (
                  <StyledTableCell key={`columnItem-${i}`}>
                    <IconButton
                      aria-label="expand row"
                      size="small"
                      onClick={() => setOpen(!open)}
                      sx={{
                        color: "#6B7280",
                        "&:hover": {
                          backgroundColor: "rgba(7, 6, 57, 0.04)",
                        },
                      }}
                    >
                      {open ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </StyledTableCell>
                );
              default:
                return (
                  <StyledTableCell
                    align={columnItem.align}
                    key={`columnItem-${i}`}
                  >
                    {columnItem.rendererType
                      ? tdRenderer(row, columnItem, i)
                      : columnItem?.field
                          ?.split(".")
                          ?.reduce(function (p, prop) {
                            return p?.[prop];
                          }, row)}
                  </StyledTableCell>
                );
                break;
            }
          })}
        </StyledTableRow>
        {exandableRow && open && (
          <TableRow>
            <StyledTableCell
              sx={{ 
                paddingBottom: 0, 
                paddingTop: 0,
                borderBottom: "none"
              }}
              colSpan={columns.length}
            >
              <Collapse in={open} timeout="auto" unmountOnExit>
                {renderExpandedRowData({ row, index })}
              </Collapse>
            </StyledTableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }

  const skeletonRowCount = getSkeletonRowCount(selectedPageSize);

  return (
    <Box id="simple-table-grid" width="100%">
      <StyledTableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow sx={{ "& > *": { borderBottom: "1px" } }}>
              {columns.map(({ title, align, width }, index) => (
                <StyledTableCell
                  sx={{ width }}
                  align={align}
                  key={`column-${index}`}
                >
                  {title}
                </StyledTableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.length
              ? rows.map((row: T, index) => (
                  <Row index={index} row={row} key={`rowItem-${index}`} />
                ))
              : fetching
              ? Array.from({ length: skeletonRowCount }).map((_, idx) => (
                  <SkeletonRow key={`skeleton-row-${idx}`} columns={columns} />
                ))
              : (
                  <StyledTableRow>
                    <StyledTableCell
                      colSpan={columns.length}
                      sx={{ borderBottom: 0, padding: 0 }}
                    >
                      <EmptyStateContainer>
                        <EmptyStateIcon />
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#6B7280",
                            fontWeight: 500,
                            marginBottom: "8px",
                          }}
                        >
                          No data available
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#9CA3AF",
                            maxWidth: "300px",
                          }}
                        >
                          There are no records to display at the moment. Check back later or try refreshing the page.
                        </Typography>
                      </EmptyStateContainer>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
          </TableBody>
        </Table>
      </StyledTableContainer>
      {rows?.length > 0 && showPagination && (
        <Box mt={1.5}>
          <PaginationContext.Provider
            value={{
              onPageChange,
              onPageSizeChange,
              totalRecords: total,
              showPageSize,
              selectedPageSize,
              selectedPage,
            }}
          >
            <Paginator />
          </PaginationContext.Provider>
        </Box>
      )}
    </Box>
  );
}
